import { useRef, useState, useCallback, useEffect } from "react";
import { useMedia } from "./useMedia";
import { useBroadcasterPeer } from "./useBroadcasterPeer";
import { useViewerPeer } from "./useViewerPeer";
import { useSignal } from "./useSignal";
import { useNetworkQuality } from "./useNetworkQuality";
import { useScreenShare } from "./useScreenShare";
import { useCallStateMachine } from "./useCallStateMachine";
import { useIceRestart } from "./useIceRestart";
import { ICE_CONFIG } from "./iceConfig";

const rtcConfig = ICE_CONFIG || { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };

export const useCallSocket = ({ user, isBroadcaster = false, onIncomingCall }) => {
  // 1. REFS & STATE
  const targetRef = useRef(null);
  const call = useCallStateMachine();
  const [activeUsers, setActiveUsers] = useState([]);
  const media = useMedia();
  const localStreamRef = useRef(null);
  
  const pcRef = useRef(null);
  const negotiatingRef = useRef(false);
  const isBroadcasterRef = useRef(isBroadcaster);
  const signalRef = useRef(null);
  const iceCandidateQueue = useRef([]);

  useEffect(() => { isBroadcasterRef.current = isBroadcaster; }, [isBroadcaster]);
  
  // Sync Ref with State (Backup)
  useEffect(() => {
    if (media.localStream) localStreamRef.current = media.localStream;
  }, [media.localStream]);

  // =================================================================
  // 2. HELPER HANDLERS (Must be defined before Peers)
  // =================================================================

  const handleIceCandidateEmit = useCallback((candidate) => {
      if (targetRef.current && signalRef.current) {
          signalRef.current.sendIce({ to: targetRef.current, candidate });
      }
  }, []);

  const handleRemoteStream = useCallback((stream) => {
      console.log("📺 [Viewer] Stream Received via Helper Hook");
      media.setRemoteStream(stream);
      call.connected();
  }, [media, call]);

  // =================================================================
  // 3. PEER INITIALIZATION (Must be defined before Toggle Handler)
  // =================================================================
  
  const broadcasterPeer = useBroadcasterPeer({ 
      pcRef, 
      onIceCandidate: handleIceCandidateEmit 
  });
  
  const viewerPeer = useViewerPeer({ 
      pcRef,
      onRemoteStream: handleRemoteStream, 
      onIceCandidate: handleIceCandidateEmit 
  });

  // ✅ DEFINED HERE so it can be used below
  const peer = isBroadcaster ? broadcasterPeer : viewerPeer;

  const safeAddIceCandidate = async (pc, candidate) => {
      if (!candidate) return;
      try {
          if (typeof candidate === 'object' && candidate.candidate) {
              await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } else if (typeof candidate === 'string') {
              await pc.addIceCandidate(new RTCIceCandidate({ candidate, sdpMid: "0", sdpMLineIndex: 0 }));
          } else {
              await pc.addIceCandidate(candidate);
          }
      } catch (e) { console.debug("ICE Retry failed", e); }
  };

  // =================================================================
  // 4. TOGGLE HANDLER (Uses 'peer')
  // =================================================================

  // ✅ ROBUST TOGGLE HANDLER (Prioritizes Hardware Kill)
  const handleRemoteToggleRequest = useCallback(async ({ enabled }) => {
    console.log(`📷 [Hardware] Received Toggle Request: ${enabled}`);
    
    // STRATEGY 1: Use Specialized Peer Toggle (HARD STOP / LIGHT OFF)
    // We check this FIRST because it handles the complex "stop() vs mute" logic
    if (isBroadcaster && peer.toggleVideo) {
        console.log("🛠️ Using Peer.toggleVideo (Hardware Kill Strategy)");
        const result = await peer.toggleVideo(enabled);
        
        // If we just turned the camera BACK ON, we got a new track. 
        // We must update the local preview immediately.
        if (enabled && result && typeof result === 'object') {
             console.log("🔄 Updating Local Preview with new Track");
             if (media.localStream) {
                 // Keep audio, replace video
                 const audioTracks = media.localStream.getAudioTracks();
                 const newStream = new MediaStream([...audioTracks, result]);
                 media.setLocalStream(newStream);
             }
        }
        return; // ✅ Stop here! We handled it.
    }

    // STRATEGY 2: Local Ref (Backup / Soft Mute)
    // This runs only if Strategy 1 failed or if we aren't the broadcaster
    if (localStreamRef.current) {
        const videoTracks = localStreamRef.current.getVideoTracks();
        if (videoTracks.length > 0) {
            const track = videoTracks[0];
            if (track.enabled === enabled && track.readyState === "live") return;
            
            track.enabled = enabled;
            console.log(`✅ [Strategy 2] Local Ref toggled to: ${enabled}`);
            return;
        }
    }

    // STRATEGY 3: Brute Force PC (Last Resort)
    if (pcRef.current) {
        const senders = pcRef.current.getSenders();
        const videoSender = senders.find(s => s.track && s.track.kind === 'video');
        if (videoSender && videoSender.track) {
             if (videoSender.track.enabled === enabled) return;
             videoSender.track.enabled = enabled;
             console.log(`✅ [Strategy 3] PC Sender toggled to: ${enabled}`);
             return;
        }
    }

    if (!enabled) console.log("⚠️ Could not find track to stop (Likely already stopped).");
    else console.error("❌ CRITICAL: Cannot turn ON. No video track found!");

  }, [isBroadcaster, peer, media]); // Added media dependency for stream update

  // =================================================================
  // 5. ACTIONS
  // =================================================================

  const stopCall = useCallback(() => {
      console.log("🛑 Stopping Call & Cleaning Up");
      if (pcRef.current) { 
          pcRef.current.oniceconnectionstatechange = null; 
          pcRef.current.close(); 
          pcRef.current = null; 
      }
      media.setRemoteStream(null);
      if (isBroadcaster) {
          console.log("📸 Stopping Local Media Hardware...");
          media.stopLocalMedia(); 
          localStreamRef.current = null; 
      } else {
          media.setLocalStream(null);
      }
      call.endCall();
      negotiatingRef.current = false;
      targetRef.current = null;
  }, [peer, media, call, isBroadcaster]);

  // =================================================================
  // 6. SIGNALING HANDLERS
  // =================================================================

  const onOfferHandler = useCallback(async ({ from, offer }) => {
      if (!isBroadcasterRef.current) return;
      
      console.log("📞 [Broadcaster] Received Offer:", from);
      if (onIncomingCall) onIncomingCall(from);
      targetRef.current = from;
      call.incomingCall();

      const pc = peer.createPC(rtcConfig);

      pc.oniceconnectionstatechange = () => {
          if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'closed') {
              console.log("⚠️ Viewer Disconnected. Stopping Camera.");
              stopCall();
          }
      };

      pc.onicecandidate = (event) => {
          if (event.candidate && signalRef.current) {
              signalRef.current.sendIce({ to: from, candidate: event.candidate });
          }
      };

      try {
          console.log("📜 [Broadcaster] Setting Remote Desc");
          await pc.setRemoteDescription(offer);

          let stream = media.localStream;
          if (!stream) {
               console.log("🎥 [Broadcaster] Auto-starting Camera...");
               stream = await media.initLocalMedia();
          }
          if (stream) {
              await peer.addLocalTracksOnce(stream);
              localStreamRef.current = stream; // Force capture
          }
          
          console.log("📝 [Broadcaster] Creating Answer");
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          
          if (signalRef.current) {
              signalRef.current.sendAnswer({ to: from, answer: pc.localDescription });
          }
          call.answerSent();
          
          const queue = iceCandidateQueue.current;
          if (queue.length > 0) {
              for (const c of queue) await safeAddIceCandidate(pc, c);
              iceCandidateQueue.current = [];
          }

      } catch(e) { console.error("Offer Error", e); }
  }, [media, call, onIncomingCall, peer, stopCall]); 

  const onAnswerHandler = useCallback(async ({ answer }) => {
      if (isBroadcasterRef.current && !negotiatingRef.current) return;
      console.log("✅ [Viewer] Received Answer");
      const pc = pcRef.current; 
      if (pc) {
          try {
              if (pc.signalingState === "stable") return;
              await pc.setRemoteDescription(new RTCSessionDescription(answer));
              call.connected();
              negotiatingRef.current = false;
              const queue = iceCandidateQueue.current;
              if (queue.length > 0) {
                  for (const c of queue) await safeAddIceCandidate(pc, c);
                  iceCandidateQueue.current = [];
              }
          } catch(e) { console.error("Answer Error:", e); }
      } 
  }, [call]);

  const onIceHandler = useCallback(({ candidate }) => {
      const pc = pcRef.current;
      if (!candidate) return;
      const canAdd = pc && pc.remoteDescription && pc.signalingState !== 'closed';
      if (canAdd) safeAddIceCandidate(pc, candidate);
      else iceCandidateQueue.current.push(candidate);
  }, []);

  const onActiveUsersHandler = useCallback((data) => {
       let userList = [];
       if (Array.isArray(data)) userList = data;
       else if (data && Array.isArray(data.users)) userList = data.users;
       const filtered = userList.filter(u => String(u.userId) !== String(user?._id));
       setActiveUsers(filtered);
  }, [user]);

  // ✅ SIGNAL HOOK (Uses Handler)
  const signal = useSignal({
      user, isBroadcaster,
      onOffer: onOfferHandler, onAnswer: onAnswerHandler, onIce: onIceHandler, onActiveUsers: onActiveUsersHandler, 
      onToggleVideo: handleRemoteToggleRequest 
  });

  useEffect(() => { signalRef.current = signal; }, [signal]);

  const callUser = useCallback(async (targetUserId) => {
      if (negotiatingRef.current) { stopCall(); }

      console.log("📡 [Viewer] Initiating Call to:", targetUserId);
      targetRef.current = targetUserId;
      negotiatingRef.current = true;
      
      if (media.setLocalStream) media.setLocalStream(null); 
      call.callStart();

      const pc = peer.createPC(rtcConfig);

      pc.ontrack = (event) => {
          let stream = event.streams[0] || new MediaStream([event.track]);
          media.setRemoteStream(stream);
          call.connected();
      };

      pc.onicecandidate = (event) => {
          if (event.candidate && signalRef.current) {
              signalRef.current.sendIce({ to: targetUserId, candidate: event.candidate });
          }
      };

      pc.addTransceiver("video", { direction: "recvonly" });
      pc.addTransceiver("audio", { direction: "recvonly" });

      try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          if (signalRef.current) {
              signalRef.current.sendOffer({ to: targetUserId, offer: pc.localDescription });
          }
      } catch(e) {
          console.error("Call Error", e);
          negotiatingRef.current = false;
          call.endCall();
      }
  }, [peer, call, stopCall, media]);

  // =================================================================
  // 7. EXPOSED API
  // =================================================================

  const sendToggleVideo = async (enabled) => {
      console.log("🔄 [Action] Toggle Video Requested:", enabled);
      if (!isBroadcaster && signal && targetRef.current) {
          signal.sendToggleVideo({ to: targetRef.current, enabled });
      }
      if (isBroadcaster) {
          if (peer.toggleVideo) {
              await peer.toggleVideo(enabled);
          } else if (localStreamRef.current) {
              localStreamRef.current.getVideoTracks().forEach(t => t.enabled = enabled);
          }
      }
  };

  const network = useNetworkQuality(pcRef); 
  const ice = useIceRestart({ pcRef, socketRef: signalRef, targetRef });
  const screen = useScreenShare({ videoSenderRef: isBroadcaster ? peer.videoSenderRef : null });

  return {
    callUser, stopCall, sendToggleVideo, 
    sendToggleAudio: (e) => signal.sendToggleAudio({ to: targetRef.current, enabled: e }),
    startScreenShare: screen.startScreenShare, stopScreenShare: screen.stopScreenShare, 
    isScreenSharing: screen.isScreenSharing, startRecording: media.startRecording, 
    stopRecording: media.stopRecording, recording: media.recording,
    restartIce: ice.restartIce, callState: call.callState,
    activeUsers, remoteStream: media.remoteStream,
    networkQuality: network.quality, networkStats: network.stats
  };
};